import React from 'react';
import { RecurrenceInput } from '../services/recurrenceService';

// Shared recurrence pattern editor — used by NewTaskModal and the task
// detail drawer. Frequency + interval + a bounded end condition (after N
// occurrences or by date; unbounded "never" is deliberately not offered —
// instance generation happens client-side at save).

export const DEFAULT_RECURRENCE: RecurrenceInput = {
  frequency: 'weekly',
  interval: 1,
  end: { type: 'count', count: 8 },
};

const fieldLabel: React.CSSProperties = {
  display: 'block',
  fontSize: 12, color: 'var(--ink-3)', marginBottom: 4,
  fontFamily: 'var(--font-ui)',
};

export default function RecurrenceControls({ value, onChange }: {
  value: RecurrenceInput;
  onChange: (v: RecurrenceInput) => void;
}) {
  const unitLabel = value.frequency === 'daily' ? 'day(s)' : value.frequency === 'weekly' ? 'week(s)' : 'month(s)';
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 6, padding: 12, background: 'var(--surface-2)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={fieldLabel}>Repeats</label>
          <select
            value={value.frequency}
            onChange={e => onChange({ ...value, frequency: e.target.value as RecurrenceInput['frequency'] })}
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
        <div>
          <label style={fieldLabel}>Every</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="number" min={1} max={99}
              value={value.interval}
              onChange={e => onChange({ ...value, interval: Math.max(1, Math.min(99, Number(e.target.value) || 1)) })}
              style={{ width: 70 }}
            />
            <span style={{ fontSize: 12, color: 'var(--ink-3)', fontFamily: 'var(--font-ui)' }}>{unitLabel}</span>
          </div>
        </div>
      </div>

      <label style={fieldLabel}>Ends</label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--ink-2)', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
          <input
            type="radio"
            checked={value.end.type === 'count'}
            onChange={() => onChange({ ...value, end: { type: 'count', count: value.end.type === 'count' ? value.end.count : 8 } })}
          />
          After
          <input
            type="number" min={1} max={100}
            disabled={value.end.type !== 'count'}
            value={value.end.type === 'count' ? value.end.count : 8}
            onChange={e => onChange({ ...value, end: { type: 'count', count: Math.max(1, Math.min(100, Number(e.target.value) || 1)) } })}
            style={{ width: 70 }}
          />
          occurrences
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--ink-2)', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
          <input
            type="radio"
            checked={value.end.type === 'date'}
            onChange={() => onChange({ ...value, end: { type: 'date', date: value.end.type === 'date' ? value.end.date : '' } })}
          />
          By date
          <input
            type="date"
            disabled={value.end.type !== 'date'}
            value={value.end.type === 'date' ? value.end.date : ''}
            onChange={e => onChange({ ...value, end: { type: 'date', date: e.target.value } })}
          />
        </label>
      </div>
    </div>
  );
}
